/* eslint-disable no-console -- a dev-only console tool; its output is the feature */
// MooTools is eval'd from a string (script-loader), so instead of hooking its
// source we snapshot the globals before it loads and proxy what it added.
import { safeLocalStorage } from '@ifixit/utils';

const FLAG_KEY = 'mootoolsTrace';
const PREFIX = '[mootools]';
const NO_FILE = 'no file';
const isProduction = process.env.NODE_ENV === 'production';

type AnyFunction = (...args: any[]) => any;

export interface TraceRow {
   name: string;
   caller: string;
   count: number;
}

export interface MooToolsTrace {
   enable(): void;
   disable(): void;
   report(): TraceRow[];
   reset(): void;
   calls: Map<string, Map<string, number>>;
}

declare global {
   interface Window {
      mootoolsTrace?: MooToolsTrace;
   }
}

interface Target {
   label: string;
   object: object;
}

// Own property names per target object, captured before MooTools loads.
type Snapshot = Map<object, Set<string>>;

// Window globals keep their value too, so a replaced global (MooTools swaps
// `Element`, `Window` and `Document` for its own types) is caught as well.
type GlobalSnapshot = Map<string, unknown>;

const calls: Map<string, Map<string, number>> = new Map();
const logged = new Set<string>();
const proxies = new WeakSet<object>();
const state = { depth: 0, installed: false, framesNameFiles: true };

function resolveTargets(): Target[] {
   const g = globalThis as any;
   const candidates: Array<[string, unknown]> = [
      ['window', g.window],
      ['Window.prototype', g.Window?.prototype],
      ['Document.prototype', g.Document?.prototype],
      ['document', g.document],
      ['Element.prototype', g.Element?.prototype],
      ['Event.prototype', g.Event?.prototype],
      ['Array', Array],
      ['Array.prototype', Array.prototype],
      ['Function', Function],
      ['Function.prototype', Function.prototype],
      ['String', String],
      ['String.prototype', String.prototype],
      ['Number', Number],
      ['Number.prototype', Number.prototype],
      ['Object', Object],
      ['Date', Date],
      ['Date.prototype', Date.prototype],
      ['RegExp.prototype', RegExp.prototype],
      ['JSON', JSON],
   ];
   return candidates
      .filter(
         (entry): entry is [string, object] =>
            (entry[1] != null && typeof entry[1] === 'object') || typeof entry[1] === 'function'
      )
      .map(([label, object]) => ({ label, object }));
}

function snapshot(): { targets: Target[]; names: Snapshot; globals: GlobalSnapshot } {
   const targets = resolveTargets();
   const names: Snapshot = new Map();
   for (const { object } of targets) {
      names.set(object, new Set(Object.getOwnPropertyNames(object)));
   }
   const globals: GlobalSnapshot = new Map();
   for (const name of Object.getOwnPropertyNames(globalThis)) {
      const desc = Object.getOwnPropertyDescriptor(globalThis, name);
      // Reading accessor globals can throw or have side effects; only data
      // properties are candidates anyway.
      if (desc && 'value' in desc) {
         globals.set(name, desc.value);
      }
   }
   return { targets, names, globals };
}

const before = isProduction ? null : snapshot();

function isTraceEnabled(): boolean {
   return safeLocalStorage.getItem(FLAG_KEY) === '1';
}

// Firefox names eval'd code that has no sourceURL `<chunk> line 69 > eval line 20 > eval:5:9`.
// In a dev build only script-loader output runs that way, which V8 frames name `script-loader`.
const TRACER_FRAME =
   /\/mootools_trace\.[jt]s\b|3P\/mootools-custom|script-loader|terser-loader| line \d+ > eval\b/;

const NOT_A_PAGE_FILE = /node_modules\/|__webpack_require__/;

function withLine(file: string, line: string | undefined): string {
   return line ? `${file}:${line}` : file;
}

// Returns the frame's file (plus line) in a form stable across builds, or null
// when the frame carries no recognizable file.
function locateFrame(frame: string): string | null {
   // eval-source-map: `at fn (webpack-internal:///./Guide/foo.js:12:3)`
   let m = /webpack(?:-internal)?:\/\/\/?((?:\.{1,2}\/)[^\s):?]+)(?::(\d+))?/.exec(frame);
   if (m) {
      return withLine(m[1], m[2]);
   }
   // Nested eval without a sourceURL: `at fn (eval at ./Guide/foo.js (...), <anonymous>:12:3)`
   m = /eval at (\.{1,2}\/\S+) \(/.exec(frame);
   if (m) {
      const line = /<anonymous>:(\d+)/.exec(frame);
      return withLine(m[1], line?.[1]);
   }
   // Absolute paths (vitest, node): keep the repo-relative tail.
   m = /((?:carpenter-frontend|packages)\/[^\s):?]+)(?::(\d+))?/.exec(frame);
   if (m) {
      return withLine(m[1], m[2]);
   }
   return null;
}

export function callerFromStack(stack: string | undefined): string {
   if (!stack) {
      return 'unknown';
   }
   for (const frame of stack.split('\n')) {
      // V8 frames start with `at`, Firefox/Safari frames contain `@`; anything
      // else is the `Error` header.
      if (!/^\s*at\s|@/.test(frame) || TRACER_FRAME.test(frame)) {
         continue;
      }
      const located = locateFrame(frame);
      if (located) {
         return located;
      }
      // A bare eval / native frame says nothing about the caller; keep looking.
      if (/<anonymous>|^\s*at (?:eval|new Proxy|Proxy\.|Reflect\.)/.test(frame)) {
         continue;
      }
      return frame.trim();
   }
   return 'unknown';
}

function stackHere(): string | undefined {
   // eslint-disable-next-line @ifixit/no-new-error, unicorn/error-message -- a stack probe, never thrown
   return new Error().stack;
}

// Safari ignores the `//# sourceURL` that eval-source-map gives every dev
// module, so no frame names a file there, not even this one.
function stackFramesNameFiles(): boolean {
   const ownFrame = (stackHere() ?? '').split('\n').find(line => /^\s*at\s|@/.test(line));
   return ownFrame !== undefined && TRACER_FRAME.test(ownFrame);
}

function record(name: string) {
   const caller = state.framesNameFiles ? callerFromStack(stackHere()) : NO_FILE;
   // MooTools calling itself while loading or from a callback: the frame past it is a library,
   // webpack's runtime or nothing, never a file to convert.
   if (caller === 'unknown' || NOT_A_PAGE_FILE.test(caller)) {
      return;
   }
   let callers = calls.get(name);
   if (!callers) {
      callers = new Map();
      calls.set(name, callers);
   }
   callers.set(caller, (callers.get(caller) ?? 0) + 1);
   const key = `${name}\n${caller}`;
   if (!logged.has(key)) {
      logged.add(key);
      console.log(`${PREFIX} %s from %s`, name, caller);
   }
}

function wrapFunction(fn: AnyFunction, name: string): AnyFunction {
   const proxy = new Proxy(fn, {
      apply(target, thisArg, args) {
         if (state.depth === 0) {
            record(name);
         }
         state.depth++;
         try {
            return Reflect.apply(target, thisArg, args);
         } finally {
            state.depth--;
         }
      },
      construct(target, args, newTarget) {
         if (state.depth === 0) {
            record(name);
         }
         state.depth++;
         try {
            return Reflect.construct(target, args, newTarget);
         } finally {
            state.depth--;
         }
      },
   });
   proxies.add(proxy);
   return proxy;
}

// Replaces `object[prop]` with a logging proxy when it is a writable or
// configurable function-valued data property.
function wrapProperty(object: object, prop: string, label: string) {
   const desc = Object.getOwnPropertyDescriptor(object, prop);
   if (!desc || !('value' in desc) || typeof desc.value !== 'function') {
      return;
   }
   if (proxies.has(desc.value) || (!desc.writable && !desc.configurable)) {
      return;
   }
   const value = wrapFunction(desc.value, `${label}.${prop}`);
   if (desc.writable) {
      (object as any)[prop] = value;
   } else {
      Object.defineProperty(object, prop, { ...desc, value });
   }
}

// Wraps every function-valued own property of `object` except `skip` names.
function wrapOwnFunctions(object: object, label: string, skip?: Set<string>) {
   for (const prop of Object.getOwnPropertyNames(object)) {
      if (prop === 'constructor' || skip?.has(prop)) {
         continue;
      }
      wrapProperty(object, prop, label);
   }
}

function isPlainObject(value: unknown): value is object {
   if (typeof value !== 'object' || value === null) {
      return false;
   }
   const proto = Object.getPrototypeOf(value);
   return proto === Object.prototype || proto === null;
}

// A MooTools type or class: wrap its statics and instance methods one level
// deep, then the global itself, so constructing a Class and calling $$ log.
function wrapGlobal(name: string, value: unknown, names: Snapshot) {
   if (typeof value === 'function') {
      wrapOwnFunctions(value, name, names.get(value) ?? new Set(['length', 'name', 'prototype']));
      const proto = value.prototype;
      if (typeof proto === 'object' && proto !== null) {
         wrapOwnFunctions(proto, `${name}.prototype`, names.get(proto));
      }
      wrapProperty(globalThis, name, 'window');
   } else if (isPlainObject(value)) {
      wrapOwnFunctions(value, name, names.get(value));
   }
}

function wrapAdded() {
   if (!before) {
      return;
   }
   const { targets, names, globals } = before;
   for (const { label, object } of targets) {
      if (object === globalThis) {
         continue;
      }
      wrapOwnFunctions(object, label, names.get(object));
   }
   for (const name of Object.getOwnPropertyNames(globalThis)) {
      const desc = Object.getOwnPropertyDescriptor(globalThis, name);
      if (!desc || !('value' in desc)) {
         continue;
      }
      const isNew = !globals.has(name);
      const isReplaced = !isNew && globals.get(name) !== desc.value;
      if (isNew || isReplaced) {
         wrapGlobal(name, desc.value, names);
      }
   }
}

function report(): TraceRow[] {
   const rows: TraceRow[] = [];
   for (const [name, callers] of calls) {
      for (const [caller, count] of callers) {
         rows.push({ name, caller, count });
      }
   }
   rows.sort((a, b) => a.name.localeCompare(b.name) || a.caller.localeCompare(b.caller));
   console.table(rows);
   return rows;
}

function reset() {
   calls.clear();
   logged.clear();
}

function enable() {
   safeLocalStorage.setItem(FLAG_KEY, '1');
   console.log(`${PREFIX} tracing enabled; reload the page to start logging`);
}

function disable() {
   safeLocalStorage.removeItem(FLAG_KEY);
   console.log(`${PREFIX} tracing disabled; reload the page to stop logging`);
}

// Dev builds always expose window.mootoolsTrace; APIs are wrapped only when
// the persisted flag is on, so untraced dev pages pay nothing.
export function installMooToolsTrace() {
   if (!before || state.installed) {
      return;
   }
   state.installed = true;
   const enabled = isTraceEnabled();
   if (enabled) {
      state.framesNameFiles = stackFramesNameFiles();
      wrapAdded();
   }
   // Assigned after wrapping: a new plain-object global would get wrapped too.
   window.mootoolsTrace = { enable, disable, report, reset, calls };
   if (!enabled) {
      return;
   }
   console.log(
      `${PREFIX} tracing on; run mootoolsTrace.report() for the table, mootoolsTrace.disable() to stop`
   );
   if (!state.framesNameFiles) {
      console.warn(
         `${PREFIX} this browser's stack frames name no files in a dev build, so every caller reads "${NO_FILE}"; use Chrome or Firefox to see the calling file`
      );
   }
}
